import type {
  ApplicationDetail,
  ApiKeyView,
  CatalogFilters,
  Category,
  CreateInstallationResult,
  CryptoPayment,
  CurrentSession,
  DeveloperApplicationDetail,
  DeveloperApplicationRow,
  DeveloperOverview,
  DeveloperProfile,
  DeveloperVersionRow,
  DiscoveryFeed,
  Installation,
  InstallationPreview,
  InstallationStatus,
  IssuedApiKey,
  Paged,
  PublisherSale,
  Review,
  ReviewSort,
  SecurityReport,
  SubmitReviewResult,
  VersionSummary,
  ApplicationCard,
} from './types';

/**
 * The single place the browser talks to the API.
 *
 * Everything goes through `request`, so credentials, error translation and abort handling exist
 * once rather than at ninety call sites. There is no token handling here at all: the session is an
 * HttpOnly cookie the API sets, which is why every call passes `credentials: 'include'` and why an
 * XSS bug cannot walk away with a session (§34).
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  /** Field-level messages from the API's validator, keyed by field path. */
  readonly fields: Readonly<Record<string, string>>;

  constructor(input: {
    status: number;
    code: string;
    message: string;
    requestId?: string | undefined;
    fields?: Readonly<Record<string, string>> | undefined;
  }) {
    super(input.message);
    this.name = 'ApiError';
    this.status = input.status;
    this.code = input.code;
    this.requestId = input.requestId;
    this.fields = input.fields ?? {};
  }

  /** True when signing in would plausibly fix it, which is the only case worth redirecting on. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** A 5xx or a network failure is worth offering a retry for; a 4xx is not. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }
}

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal | undefined;
  readonly headers?: Readonly<Record<string, string>>;
  /** Raw bytes instead of JSON, for image uploads. */
  readonly binaryBody?: ArrayBuffer | Blob | undefined;
}

const BASE = `${(import.meta.env.VITE_API_ORIGIN as string | undefined)?.replace(/\/$/, '') ?? ''}/v1`;

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', ...options.headers };
  let body: BodyInit | undefined;

  if (options.binaryBody !== undefined) {
    body = options.binaryBody;
  } else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      // Required for the session cookie to be sent, and the reason CORS on the API is origin-bound.
      credentials: 'include',
      headers,
      ...(body === undefined ? {} : { body }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    // An aborted request is a normal part of navigation, so it is rethrown untouched rather than
    // being reported to the user as a failure.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError({
      status: 0,
      code: 'NETWORK_ERROR',
      message: 'Could not reach the marketplace. Check your connection and try again.',
    });
  }

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => undefined);
  if (!response.ok) throw toApiError(response.status, payload);
  return payload as T;
}

function toApiError(status: number, payload: unknown): ApiError {
  const error = readErrorEnvelope(payload);
  if (error === undefined) {
    return new ApiError({
      status,
      code: 'UNKNOWN',
      message: 'Something went wrong. Please try again.',
    });
  }
  return new ApiError({
    status,
    code: error.code,
    message: error.message,
    requestId: error.requestId,
    fields: error.fields,
  });
}

/**
 * Reads the API's error envelope defensively.
 *
 * A failing response is exactly the situation in which the body may not be the shape we expect —
 * a proxy error page, a truncated response — so nothing here assumes it is.
 */
function readErrorEnvelope(payload: unknown):
  | {
      code: string;
      message: string;
      requestId: string | undefined;
      fields: Record<string, string>;
    }
  | undefined {
  if (typeof payload !== 'object' || payload === null || !('error' in payload)) return undefined;
  const error: unknown = (payload as { error: unknown }).error;
  if (typeof error !== 'object' || error === null) return undefined;

  const record = error as Record<string, unknown>;
  const fields: Record<string, string> = {};
  if (Array.isArray(record['fields'])) {
    for (const entry of record['fields']) {
      if (typeof entry !== 'object' || entry === null) continue;
      const { path, message } = entry as Record<string, unknown>;
      if (typeof path === 'string' && typeof message === 'string') fields[path] = message;
    }
  }

  return {
    code: typeof record['code'] === 'string' ? record['code'] : 'UNKNOWN',
    message:
      typeof record['message'] === 'string'
        ? record['message']
        : 'Something went wrong. Please try again.',
    requestId: typeof record['requestId'] === 'string' ? record['requestId'] : undefined,
    fields,
  };
}

/**
 * Serializes filters into a query string.
 *
 * Empty and default-valued entries are dropped so that a bookmarked URL only contains the filters
 * the user actually chose, and so identical filter sets produce identical URLs — which is what
 * makes the API's cache keys hit.
 */
export function toQueryString(
  params: Readonly<Record<string, string | number | boolean | readonly string[] | undefined>>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === false) continue;
    if (Array.isArray(value)) {
      for (const entry of value) search.append(key, entry);
    } else {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query === '' ? '' : `?${query}`;
}

/**
 * A key that makes a resubmitted install a no-op.
 *
 * Generated once per wizard session and reused across retries, which is the entire point: a
 * fresh key on every attempt would let a double-click provision two sets of billable resources.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export const api = {
  discover(signal?: AbortSignal): Promise<DiscoveryFeed> {
    return request<DiscoveryFeed>('/discover', { signal });
  },

  categories(signal?: AbortSignal): Promise<{ items: readonly Category[] }> {
    return request<{ items: readonly Category[] }>('/categories', { signal });
  },

  browse(
    filters: CatalogFilters,
    page: { limit?: number; cursor?: string | undefined } = {},
    signal?: AbortSignal,
  ): Promise<Paged<ApplicationCard>> {
    const query = toQueryString({
      q: filters.q,
      category: filters.category,
      tag: filters.tag,
      type: filters.type,
      pricing: filters.pricing,
      developer: filters.developer,
      minRating: filters.minRating,
      verifiedOnly: filters.verifiedOnly,
      safeOnly: filters.safeOnly,
      sort: filters.sort,
      limit: page.limit,
      cursor: page.cursor,
    });
    return request<Paged<ApplicationCard>>(`/applications${query}`, { signal });
  },

  application(slug: string, signal?: AbortSignal): Promise<ApplicationDetail> {
    return request<ApplicationDetail>(`/applications/${encodeURIComponent(slug)}`, { signal });
  },

  applicationVersions(
    slug: string,
    page: { cursor?: string | undefined } = {},
    signal?: AbortSignal,
  ): Promise<Paged<VersionSummary>> {
    return request<Paged<VersionSummary>>(
      `/applications/${encodeURIComponent(slug)}/versions${toQueryString({ cursor: page.cursor })}`,
      { signal },
    );
  },

  securityReport(
    slug: string,
    versionId?: string | undefined,
    signal?: AbortSignal,
  ): Promise<SecurityReport> {
    return request<SecurityReport>(
      `/applications/${encodeURIComponent(slug)}/security${toQueryString({ versionId })}`,
      { signal },
    );
  },

  reviews(
    slug: string,
    options: { sort?: ReviewSort; cursor?: string | undefined } = {},
    signal?: AbortSignal,
  ): Promise<Paged<Review>> {
    return request<Paged<Review>>(
      `/applications/${encodeURIComponent(slug)}/reviews${toQueryString({
        sort: options.sort,
        cursor: options.cursor,
      })}`,
      { signal },
    );
  },

  submitReview(
    slug: string,
    body: { rating: number; title: string; body: string },
  ): Promise<SubmitReviewResult> {
    return request<SubmitReviewResult>(`/applications/${encodeURIComponent(slug)}/reviews`, {
      method: 'POST',
      body,
    });
  },

  developerProfile(slug: string, signal?: AbortSignal): Promise<DeveloperProfile> {
    return request<DeveloperProfile>(`/developers/${encodeURIComponent(slug)}`, { signal });
  },

  session(signal?: AbortSignal): Promise<CurrentSession> {
    return request<CurrentSession>('/me', { signal });
  },

  signOut(): Promise<void> {
    return request<void>('/auth/signout', { method: 'POST' });
  },

  signOutEverywhere(): Promise<void> {
    return request<void>('/auth/signout-everywhere', { method: 'POST' });
  },

  cloudflareAccounts(signal?: AbortSignal): Promise<{
    accounts: readonly { accountId: string; accountName: string }[];
  }> {
    return request('/cloudflare/accounts', { signal });
  },

  disconnectCloudflareAccount(accountId: string): Promise<void> {
    return request<void>(`/cloudflare/accounts/${encodeURIComponent(accountId)}`, {
      method: 'DELETE',
    });
  },

  installPreview(slug: string, signal?: AbortSignal): Promise<InstallationPreview> {
    return request<InstallationPreview>(
      `/applications/${encodeURIComponent(slug)}/install-preview`,
      { signal },
    );
  },

  createInstallation(
    body: {
      applicationSlug: string;
      cloudflareAccountId: string;
      instanceName: string;
      configuration: Record<string, string>;
      secrets: Record<string, string>;
      existingResources: Record<string, string>;
      routePattern?: string | undefined;
    },
    idempotencyKey: string,
  ): Promise<CreateInstallationResult> {
    return request<CreateInstallationResult>('/installations', {
      method: 'POST',
      body,
      headers: { 'idempotency-key': idempotencyKey },
    });
  },

  installations(
    options: {
      status?: InstallationStatus | undefined;
      inFlight?: boolean | undefined;
      cursor?: string | undefined;
    } = {},
    signal?: AbortSignal,
  ): Promise<Paged<Installation>> {
    return request<Paged<Installation>>(
      `/installations${toQueryString({
        status: options.status,
        inFlight: options.inFlight === true ? 'true' : undefined,
        cursor: options.cursor,
      })}`,
      { signal },
    );
  },

  installation(id: string, signal?: AbortSignal): Promise<Installation> {
    return request<Installation>(`/installations/${encodeURIComponent(id)}`, { signal });
  },

  renameInstallation(id: string, instanceName: string): Promise<void> {
    return request<void>(`/installations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { instanceName },
    });
  },

  retryInstallation(id: string): Promise<{ stepIndex: number }> {
    return request<{ stepIndex: number }>(`/installations/${encodeURIComponent(id)}/retry`, {
      method: 'POST',
    });
  },

  rollBackInstallation(id: string): Promise<{ removedResourceCount: number }> {
    return request<{ removedResourceCount: number }>(
      `/installations/${encodeURIComponent(id)}/rollback`,
      { method: 'POST' },
    );
  },

  uninstall(id: string): Promise<{ removedResourceCount: number }> {
    return request<{ removedResourceCount: number }>(`/installations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  setPublisherWallet(address: string | undefined): Promise<{ trc20UsdtAddress: string | undefined }> {
    return request('/developer/payout-wallet', {
      method: 'PATCH',
      body: { address },
    });
  },

  createCryptoPayment(applicationId: string): Promise<CryptoPayment> {
    return request<CryptoPayment>('/payments/crypto', {
      method: 'POST',
      body: { applicationId },
    });
  },

  getCryptoPayment(paymentId: string, signal?: AbortSignal): Promise<CryptoPayment> {
    return request<CryptoPayment>(`/payments/crypto/${encodeURIComponent(paymentId)}`, { signal });
  },

  publisherSales(signal?: AbortSignal): Promise<{ items: PublisherSale[] }> {
    return request<{ items: PublisherSale[] }>('/developer/sales', { signal });
  },

  registerDeveloper(body: {
    slug: string;
    displayName: string;
    bio?: string;
    websiteUrl?: string | undefined;
    supportEmail?: string | undefined;
  }): Promise<{ developerId: string; slug: string }> {
    return request('/developer', { method: 'POST', body });
  },

  developerOverview(signal?: AbortSignal): Promise<DeveloperOverview> {
    return request<DeveloperOverview>('/developer/overview', { signal });
  },

  developerApplications(
    options: { status?: string | undefined; cursor?: string | undefined } = {},
    signal?: AbortSignal,
  ): Promise<Paged<DeveloperApplicationRow>> {
    return request<Paged<DeveloperApplicationRow>>(
      `/developer/applications${toQueryString({ status: options.status, cursor: options.cursor })}`,
      { signal },
    );
  },

  createApplicationDraft(body: {
    name: string;
    slug?: string | undefined;
    summary: string;
    description?: string;
    type: string;
    categorySlug: string;
    tags?: readonly string[];
    homepageUrl?: string | undefined;
    repositoryUrl?: string | undefined;
    supportUrl?: string | undefined;
  }): Promise<{ applicationId: string; slug: string }> {
    return request('/developer/applications', { method: 'POST', body });
  },

  updateApplicationListing(
    applicationId: string,
    body: {
      name: string;
      summary: string;
      description?: string;
      categorySlug: string;
      tags?: readonly string[];
      homepageUrl?: string | undefined;
      repositoryUrl?: string | undefined;
      supportUrl?: string | undefined;
      pricing?:
        | {
            model: string;
            amountInMinorUnits?: number | undefined;
            currency?: string | undefined;
            billingPeriod?: string | undefined;
            usageDescription?: string | undefined;
            trialDays?: number | undefined;
          }
        | undefined;
    },
  ): Promise<void> {
    return request<void>(`/developer/applications/${encodeURIComponent(applicationId)}`, {
      method: 'PATCH',
      body,
    });
  },

  developerApplication(
    applicationId: string,
    signal?: AbortSignal,
  ): Promise<DeveloperApplicationDetail> {
    return request<DeveloperApplicationDetail>(
      `/developer/applications/${encodeURIComponent(applicationId)}`,
      { signal },
    );
  },

  developerVersions(
    applicationId: string,
    options: { cursor?: string | undefined } = {},
    signal?: AbortSignal,
  ): Promise<Paged<DeveloperVersionRow>> {
    return request<Paged<DeveloperVersionRow>>(
      `/developer/applications/${encodeURIComponent(applicationId)}/versions${toQueryString({
        cursor: options.cursor,
      })}`,
      { signal },
    );
  },

  submitVersion(
    applicationId: string,
    body: {
      version: string;
      repositoryUrl: string;
      reference: string;
      subdirectory?: string | undefined;
      changelog?: string | undefined;
    },
  ): Promise<{
    applicationVersionId: string;
    version: string;
    commitSha: string;
    reusedExistingAnalysis: boolean;
  }> {
    return request(`/developer/applications/${encodeURIComponent(applicationId)}/versions`, {
      method: 'POST',
      body,
    });
  },

  publishVersion(versionId: string): Promise<{ slug: string; version: string }> {
    return request(`/developer/versions/${encodeURIComponent(versionId)}/publish`, {
      method: 'POST',
    });
  },

  uploadLogo(applicationId: string, file: Blob): Promise<{ url: string; objectKey: string }> {
    return request(`/developer/applications/${encodeURIComponent(applicationId)}/logo`, {
      method: 'POST',
      binaryBody: file,
      headers: { 'content-type': file.type },
    });
  },

  addScreenshot(
    applicationId: string,
    file: Blob,
    caption?: string,
  ): Promise<{ url: string; objectKey: string }> {
    return request(
      `/developer/applications/${encodeURIComponent(applicationId)}/screenshots${toQueryString({
        caption,
      })}`,
      { method: 'POST', binaryBody: file, headers: { 'content-type': file.type } },
    );
  },

  removeScreenshot(applicationId: string, screenshotId: string): Promise<void> {
    return request<void>(
      `/developer/applications/${encodeURIComponent(applicationId)}/screenshots/${encodeURIComponent(screenshotId)}`,
      { method: 'DELETE' },
    );
  },

  respondToReview(reviewId: string, body: string): Promise<void> {
    return request<void>(`/developer/reviews/${encodeURIComponent(reviewId)}/response`, {
      method: 'POST',
      body: { body },
    });
  },

  apiKeys(signal?: AbortSignal): Promise<{ items: readonly ApiKeyView[] }> {
    return request('/account/api-keys', { signal });
  },

  createApiKey(body: {
    name: string;
    scopes: readonly string[];
    expiresInDays: number | undefined;
  }): Promise<IssuedApiKey> {
    return request('/account/api-keys', { method: 'POST', body });
  },

  revokeApiKey(apiKeyId: string): Promise<void> {
    return request<void>(`/account/api-keys/${encodeURIComponent(apiKeyId)}`, { method: 'DELETE' });
  },
};

/**
 * Sign-in and account connection are full-page navigations, not fetches.
 *
 * Both are OAuth redirects driven entirely by the server, so the browser has to leave the page.
 * `returnTo` is a path only — the API refuses anything else — so the flow cannot be turned into an
 * open redirect.
 */
export const authUrls = {
  /** Primary marketplace sign-in — Cloudflare OAuth. */
  cloudflareSignIn(returnTo: string): string {
    return `${BASE}/auth/cloudflare/start${toQueryString({ returnTo })}`;
  },
  /** Same endpoint; when already signed in this refreshes/connects accounts. */
  connectCloudflare(returnTo: string): string {
    return `${BASE}/auth/cloudflare/start${toQueryString({ returnTo })}`;
  },
  /** Publishers only — attach GitHub while keeping the Cloudflare session. */
  connectGithub(returnTo: string): string {
    return `${BASE}/auth/github/start${toQueryString({ returnTo })}`;
  },
};
